/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { Fragment, memo, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Grid,
  MenuToggleButton,
  Button,
  Fixed,
  useBreakpointIndex,
  useThemeUI,
} from '@tishman/components';
import * as Icons from '@tishman/icons';
import { useLocation } from '@reach/router';

// import { useI18n } from '../../utils';
// import { selectFlow, useAppSelector } from '../../store';
// import { isRinkOrRinkMembershipRelated } from '../../services/viva/utils/is-rink-or-rink-membership-related';
import { AddToCartNav } from '../AddToCartNav';

import { LocaleSwitcher } from './LocaleSwitcher';
import { MenuBarLogo } from './MenuBarLogo';

import type { PropsWithChildren, ReactNode } from 'react';
import type { GridProps, LinkProps } from '@tishman/components';
import type { LogoType } from './MenuBarLogo';

export const MENU = 'MENU';
export const SEARCH = 'SEARCH';

export type OverlayType = typeof MENU | typeof SEARCH;

const MenuBarRow = ({ children, sx, ...props }: Omit<GridProps, 'ref'>) => (
  <Grid
    gap={2}
    sx={{
      gridAutoFlow: 'column',
      gridAutoColumns: 'max-content',
      alignItems: 'center',
      minHeight: '69px',
      ...sx,
    }}
    {...props}
  >
    {children}
  </Grid>
);

export type MenuBarProps<LogoLinkState = unknown> = PropsWithChildren<{
  /**
   * The logo to show in the `MenuBar`.
   *
   * Defaults to 'Rockefeller Center'
   */
  logo?: LogoType;
  /**
   * The link to follow when the logo is clicked.
   * Set this to `null` to have no link.
   *
   * Defaults to the Rockefeller Center homepage.
   */
  logoLink?: {
    /** The path the Logo links to. */
    to: string;
    /** The label for the link (will be a title). */
    label: string;
    /** Optional state to add to location state when the logo is clicked. */
    state?: LinkProps<LogoLinkState>['state'];
  } | null;
  /**
   * An optional short name for this page, .e.g., 'Home', or 'Bar SixtyFive'.
   * If provided, this name _may_ appear underneath the logo, unless
   * the logo has its own logo type.
   */
  pageName?: string | null;

  /**
   * Optional navigation item to appear on the left side of the `MenuBar`.
   * Visually, this is typically a back arrow button.
   *
   * Note that, if provided, this will _replace_ the Menu toggle button!
   */
  leftNav?: ReactNode;
  /**
   * Optional navigation item to appear on the right side of the `MenuBar`,
   * _before_ the Search toggle button. Visually, this is typically a text link.
   *
   * Note: This will be hidden on small screens!
   */
  rightNav?: ReactNode;
  /**
   * Optional navigation item to appear on the right side of the `MenuBar`,
   * _after_ the Search toggle Button. Visually, this is typically a button.
   *
   * Note: This will be hidden on small screens!
   */
  cta?: ReactNode;
  /**
   * Optional navigation item to appear on the right side of the `MenuBar`,
   * _after_ the Search toggle Button. Visually, this is typically a button.
   *
   * Note: This will be hidden on small screens!
   */
  secondaryCta?: ReactNode;
  /**
   *
   * Whether or not to hide the search toggle button in the `MenuBar`.
   */
  hideSearchToggleButton?: boolean;
  /**
   * What type of overlay is currently open, or `false` if no overlay is open.
   */
  isOpen?: false | OverlayType;
  /**
   * A callback for handling requests to open overlays from menu nav clicks,
   * i.e., from `MENU` or `SEARCH`.
   */
  onOpen?: (type: OverlayType) => void;
  /**
   * A callback for handling requests to close overlays from nav clicks.
   */
  onClose?: () => void;
  /**
   * The color of the nav bar when the menu is open
   * If undefined, no color is used
   **/
  menuOpenBGColor?: string;
}>;

/**
 * Default logo to show when now logo has been configured,
 * or when the nav is open.
 */
const defaultLogo = 'Rockefeller Center';

/**
 * Default link to wrap the logo in when no link has been configured,
 * or when the nav is open.
 */
const defaultLogoLink = {
  to: '/',
  label: 'Rockefeller Center',
};

/**
 * The RC `MenuBar` is a grid row with:
 *
 * - Left aligned grid row of navigation with:
 *   - A menu toggle button (hamburger)
 *     - The menu toggle button may replaced with a navigation item
 *       via the `leftNav` prop.
 * - The current page logo, optionally with the current page name
 *   via the `pageName` prop
 * - Right aligned grid row of navigation with:
 *   - Optional nav item via the `rightNav` prop
 *   - A search button
 *   - Optional nav item via the `cta` prop
 */
export const MenuBar = memo(
  ({
    logo: logo_ = defaultLogo,
    logoLink = defaultLogoLink,
    hideSearchToggleButton = false,
    pageName,
    leftNav,
    rightNav,
    cta,
    secondaryCta,
    isOpen,
    onClose,
    onOpen,
    menuOpenBGColor,
  }: MenuBarProps): React.JSX.Element => {
    // const { t } = useI18n();
    const { pathname } = useLocation();
    const isMobile = useBreakpointIndex() < 2;
    const themeUI = useThemeUI();

    // const checkoutFlow = useAppSelector(selectFlow);
    // const logo =
    //   /\/buy-tickets\/confirmation\//.test(pathname) &&
    //   isRinkOrRinkMembershipRelated(checkoutFlow)
    //     ? 'The Rink'
    //     : logo_;

    const logo = logo_;

    const ctaMobile = useMemo(() => {
      if (cta)
        return (
          <Fixed
            bottom={0}
            left={0}
            right={0}
            style={{
              height: themeUI.theme.sizes.ctaMobile,
              display: isMobile ? 'grid' : 'none',
              placeContent: 'stretch',
              textAlign: 'center',
              zIndex: themeUI.theme.zIndices[3],
            }}
          >
            {cta}
          </Fixed>
        );
    }, [cta, isMobile, themeUI.theme.sizes.ctaMobile, themeUI.theme.zIndices]);

    return (
      <Fragment>
        <Grid
          columns={['2fr 1fr 2fr', null, null, '1fr 1fr 1fr']}
          data-module="MenuBar"
          gap={0}
          pt={[0, 4]}
          px={[0, 3, 4]}
          sx={{
            height: [111, 150, 150, 155],
            width: '100%',
            alignItems: 'start',
            position: 'relative',
            zIndex: 'sticky',
            backgroundColor:
              isOpen === MENU && menuOpenBGColor ? menuOpenBGColor : 'none',
            ...(menuOpenBGColor && isOpen !== MENU
              ? {
                  transition: '0.5s background-color ease-out',
                }
              : {}),
          }}
        >
          <MenuBarRow data-section="MenuBarLeft">
            {leftNav ?? (
              <MenuToggleButton
                isOpen={isOpen === MENU}
                label={'menu'}
                onOpenChange={(v) => (v ? onOpen?.(MENU) : onClose?.())}
              />
            )}
          </MenuBarRow>
          <MenuBarLogo
            data-section="MenuBarLogo"
            link={isOpen === MENU ? defaultLogoLink : logoLink}
            logo={isOpen === MENU ? defaultLogo : logo}
            pageName={isOpen === MENU ? undefined : pageName}
          />
          <MenuBarRow data-section="MenuBarRight" sx={{ justifySelf: 'end' }}>
            {rightNav ? (
              <MenuBarRow sx={{ display: ['none', null, null, 'inherit'] }}>
                {rightNav}
              </MenuBarRow>
            ) : null}
            <MenuBarRow>
              <LocaleSwitcher onClose={onClose} />
            </MenuBarRow>
            {hideSearchToggleButton ? null : (
              <Button
                aria-expanded={isOpen === SEARCH}
                aria-label="Search"
                onClick={() => onOpen?.(SEARCH)}
                sx={{
                  px: ['8px', '16px'],
                  top: ['3px', '4px'],
                  left: ['0px', '8px'],
                }}
                variant="iconUnderline"
              >
                <Icons.Search aria-hidden />
              </Button>
            )}
            <MenuBarRow>
              <AddToCartNav />
            </MenuBarRow>
            {cta ? (
              <MenuBarRow
                sx={{
                  display: secondaryCta
                    ? ['none', null, null, 'inherit']
                    : ['none', null, 'inherit'],
                  gridGap: '24px',
                }}
              >
                {cta}
                {secondaryCta && secondaryCta}
              </MenuBarRow>
            ) : null}
          </MenuBarRow>
        </Grid>
        {/* a11y: putting the ctaMobile in last item in the dom order,
        it doesn't change normal ui functionality because the ctaMobile is fixed html element */}
        {typeof document !== 'undefined' && ctaMobile
          ? createPortal(ctaMobile, document.body)
          : null}
      </Fragment>
    );
  },
);

MenuBar.displayName = 'MenuBar';
